import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Modal, Table, Popconfirm, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useHrAccountMapping } from '../../hooks/useHrAccountMapping';
import HrAccountMappingForm from '../../components/HrAccountMappingForm';
import { HrAccountMapping, HrMappingType } from '../../types';

const HR_MAPPING_LABELS: Record<HrMappingType, string> = {
  SALARY_EXPENSE: 'Salary Expense (Dr)',
  ALLOWANCES_EXPENSE: 'Allowances Expense (Dr)',
  BONUS_EXPENSE: 'Bonus Expense (Dr)',
  PAYE_TAX_LIABILITY: 'PAYE Tax Liability (Cr)',
  NSSF_EMPLOYER_EXPENSE: 'NSSF Employer Expense (Dr)',
  NSSF_EMPLOYEE_LIABILITY: 'NSSF Employee Liability (Cr)',
  NHIF_EMPLOYEE_LIABILITY: 'NHIF Employee Liability (Cr)',
  NET_PAY_LIABILITY: 'Net Pay Liability (Cr)',
  SALARY_ADVANCE_ASSET: 'Salary Advance Asset (Dr)',
};

const ALL_TYPES = Object.keys(HR_MAPPING_LABELS) as HrMappingType[];

const HrAccountMappingView: React.FC = () => {
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<HrAccountMapping> | null>(null);
  const { mappings, loading, fetchMappings, saveMapping, deleteMapping } = useHrAccountMapping();

  useEffect(() => { fetchMappings(); }, []);

  const mappedTypes = new Set(mappings.map((m) => m.mappingType));
  const unmappedCount = ALL_TYPES.filter((t) => !mappedTypes.has(t)).length;

  const handleOpen = (item?: HrAccountMapping) => {
    form.resetFields();
    setEditItem(item ?? null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    await saveMapping({ ...editItem, ...values });
    setModalOpen(false);
    form.resetFields();
  };

  const columns = [
    {
      title: 'Mapping Type', dataIndex: 'mappingType', key: 'mappingType',
      render: (t: HrMappingType) => HR_MAPPING_LABELS[t] ?? t,
    },
    { title: 'GL No.', dataIndex: 'glNo', key: 'glNo', width: 90 },
    { title: 'AC No.', dataIndex: 'acNo', key: 'acNo', width: 90 },
    { title: 'Description', dataIndex: 'name', key: 'name' },
    { title: 'Branch', dataIndex: 'branch', key: 'branch', width: 80 },
    {
      title: 'Active', dataIndex: 'active', key: 'active', width: 70,
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 100,
      render: (_: any, record: HrAccountMapping) => (
        <span>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleOpen(record)} style={{ marginRight: 4 }} />
          <Popconfirm title="Delete this mapping?" onConfirm={() => deleteMapping(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Card
        title={
          <span>
            HR GL Account Mappings
            {unmappedCount > 0 && (
              <Tag color="red" style={{ marginLeft: 12 }}>{unmappedCount} unmapped</Tag>
            )}
          </span>
        }
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpen()}>Add Mapping</Button>}
      >
        {unmappedCount > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Tag color="orange">Unmapped types will be skipped during payroll GL posting.</Tag>
          </div>
        )}
        <Table dataSource={mappings} columns={columns} rowKey="id" loading={loading} size="small" />
      </Card>

      <Modal
        title={editItem?.id ? 'Edit GL Mapping' : 'New GL Mapping'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Save"
      >
        <HrAccountMappingForm form={form} initial={editItem ?? undefined} />
      </Modal>
    </div>
  );
};

export default HrAccountMappingView;
